import React from 'react';

import type { UserProfile } from '@mint-lib/profile-helpers';
import { Slot } from '@mint-lib/slots';
import styled from '@mint-lib/styled';
import type {} from '@mint-lib/theme/src/extensions/palette.js';

import {
  AppBar,
  AppBarProps,
  Avatar,
  AvatarSize,
  Box,
  Popover,
  Typography,
} from '@myn/mui';

type TopBarProps = AppBarProps & {
  title?: string;
  popoverContent?: React.ReactNode;
  onAvatarClick?: (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => void;
  onPopoverClose?: () => void;
  profileData?: UserProfile;
  clientName?: string;
  isPopoverOpen?: HTMLButtonElement | null;
};

const TopBar = ({
  children,
  position = 'sticky',
  popoverContent,
  title,
  profileData,
  clientName,
  onAvatarClick,
  isPopoverOpen,
  onPopoverClose,
  ...props
}: TopBarProps) => {
  const id = isPopoverOpen ? 'simple-popover' : undefined;

  return (
    <StyledTopBar position={position} color="inherit" elevation={0} {...props}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          marginLeft: '1rem',
          alignItems: 'center',
        }}
      >
        <Slot name="topBarCampaignTitle" />
        <Typography variant="bodyShort01" color="text.primary">
          <Slot name="topBarTitle" />
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <AvatarButton onClick={onAvatarClick}>
          <Avatar
            bgColor="secondary"
            avatarSize={AvatarSize.large}
            firstName={profileData?.profile.firstName}
            lastName={profileData?.profile.lastName}
            email={profileData?.email}
            src={profileData?.profile.avatar}
          />
        </AvatarButton>
        <StyledPopover
          id={id}
          open={Boolean(isPopoverOpen)}
          anchorEl={isPopoverOpen}
          onClose={onPopoverClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          {popoverContent}
        </StyledPopover>
      </Box>
    </StyledTopBar>
  );
};

export default TopBar;

const StyledTopBar = styled(AppBar)(({ theme }) => ({
  display: 'flex',
  height: '3rem',
  left: '48px',
  width: 'calc(100% - 48px)',
  flexDirection: 'row',
  justifyContent: 'space-between',
  borderBottom: `1px solid ${theme.palette.ui.tertiary}`,
}));

const AvatarButton = styled('button')({
  marginRight: '0.5rem',
  width: '40px',
  height: '40px',
  padding: '0px',
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
});

const StyledPopover = styled(Popover)({
  boxShadow: 'none',
  marginTop: '0.5rem',
  marginLeft: '0.25rem',
  '.MuiPaper-root': {
    boxShadow: '0px 0px 8px rgba(34, 37, 43, 0.24)',
    borderRadius: '4px',
  },
});
